import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { Add, Button, Form, FormRow, Label, QueryDropdown } from 'basic-components';
import { DropdownService, HooksService } from 'dashboard-services';
import { globalActions } from 'primary-components';

import { creatorCurvesActions } from 'actions/curves';
import { creatorSeriesActions } from 'actions/series';
import getApiConfig from 'api/ApiConfig';
import properties from 'resources/constants/properties.json';
import { CurvesUtils } from 'utils';

import { useQueryClient } from '@tanstack/react-query';

import './CurveCreator.scss';

const emptyArray = []
const CurveCreator = () => {
  const dispatch = useDispatch(),
        { 
          groupName,
          product,
          root,
          isBasketOpen
        } = useSelector(state => state.curvesState.creatorState),
        selectedCurves = useSelector(state => state.curvesState.creatorState.selectedCurves) || emptyArray,
        source = useSelector(state => state.globalState.source),
        queryClient = useQueryClient(),
        [groupQuery, setGroupQuery] = useState(),
        getApiConfigLocal = useCallback(() => dispatch(getApiConfig()), [dispatch]),
        { fetchNextPage: fetchMoreGroups, isFetching: isFetchingGroups, isFetchingNextPage: isFetchingNextPageGroups, data: groups = emptyArray } = DropdownService.useQueryGroups({
          queryClient,
          input: groupQuery,
          getApiConfig: getApiConfigLocal,
          symbolsOnly: true,
          extraQuery: "fields.name=Forward_Curve",
          provider: source
        }),
        onChangeRedux = useCallback((val, { name }) => dispatch(creatorCurvesActions.onChange(val, name)), [dispatch]),
        onChangeRoot = useCallback(val => onChangeRedux(val, { name: "root" }), [onChangeRedux]),
        onChangeProduct = useCallback(val => {
          onChangeRoot(undefined)
          onChangeRedux(val, { name: "product" })
        }, [onChangeRedux, onChangeRoot]),
        onChangeGroupName = useCallback(val => {
          onChangeProduct(undefined)
          onChangeRedux(val, { name: "groupName" })
        }, [onChangeProduct, onChangeRedux]),
        [productValueQuery, setProductValueQuery] = useState(),
        { fetchNextPage: fetchMoreProductValues, isFetching: isFetchingProductValues, isFetchingNextPage: isFetchingNextPageProductValues, data: productValues = emptyArray } = DropdownService.useQuerySymbolValues({
          queryClient,
          input: productValueQuery,
          key: CurvesUtils.PRODUCT,
          groupName,
          metadata: true,
          useEs: properties.useEsSymbolListing === "true",
          getApiConfig: getApiConfigLocal,
          enabled: !!groupName,
          provider: source
        }),
        rootMetadatas = useMemo(() => product ? { [CurvesUtils.PRODUCT]: [product] } : {}, [product]),
        [rootValueQuery, setRootValueQuery] = useState(),
        { fetchNextPage: fetchMoreRootValues, isFetching: isFetchingRootValues, isFetchingNextPage: isFetchingNextPageRootValues, data: rootValues = emptyArray } = DropdownService.useQuerySymbolValues({
          queryClient,
          input: rootValueQuery,
          key: CurvesUtils.ROOT,
          groupName,
          metadata: true,
          metadatas: rootMetadatas,
          useEs: properties.useEsSymbolListing === "true",
          getApiConfig: getApiConfigLocal,
          enabled: !!groupName && !!product,
          provider: source
        }),
        addItem = useCallback(() => {
          dispatch(creatorCurvesActions.addCurve({
            groupName,
            product,
            root,
            source
          }))
          onChangeGroupName(undefined)
        }, [dispatch, groupName, onChangeGroupName, product, root, source]),
        addItemWithToast = useCallback(() => {
          dispatch(globalActions.getToastsStore()).warning(creatorSeriesActions.PROVIDER_MESSAGE, "Discard selection", {
            withCancel: false,
            confirmText: "Discard all",
            confirmAction: () => {
              dispatch(creatorCurvesActions.onChange([], "selectedCurves"))
              addItem()
            }
          })
        }, [addItem, dispatch]),
        onAddItem = useCallback(() => {
          if(selectedCurves?.length > 0 && selectedCurves[0]?.source !== source) {
            addItemWithToast()
          } else {
            addItem()
          }
        }, [addItem, addItemWithToast, selectedCurves, source]),
        prevGroupName = HooksService.usePrevious(groupName),
        prevProduct = HooksService.usePrevious(product),
        groupRef = useRef(),
        productRef = useRef(),
        rootRef = useRef()

  useEffect(() => {
    if(groupName !== prevGroupName) {
      productRef.current?.refreshOptions?.()
      prevGroupName && productRef?.current?.dropdown?.current?.setValue()
      !groupName && groupRef?.current?.dropdown?.current?.setValue()
      !product && productRef.current?.dropdown?.current?.focus?.()
    }
  }, [groupName, prevGroupName, product])

  useEffect(() => {
    if(product !== prevProduct) {
      rootRef.current?.refreshOptions?.()
      prevProduct && rootRef?.current?.dropdown?.current?.setValue()
      !root && rootRef.current?.dropdown?.current?.focus?.()
    }
  }, [prevProduct, product, root])

  return (
    <Fragment>
      <Form className="ng-office-app__authed__content__body__item__curve-creator">
        <FormRow 
            className="ng-office-app__authed__content__body__item__curve-creator__row"
            isVertical
        >
          <Label>GROUP</Label>
          <QueryDropdown
              autoFocus={!groupName && !isBasketOpen}
              defaultValue={groupName}
              fetchNextPage={fetchMoreGroups}
              isFetching={isFetchingGroups || isFetchingNextPageGroups}
              menuPlacement="bottom"
              menuPortalTarget={document.querySelector('.ng-office-app')}
              name="groupName"
              noError
              onChange={onChangeGroupName}
              onInputChange={setGroupQuery}
              openMenuOnFocus
              options={groups}
              placeholder="Choose source group"
              ref={groupRef}
              variant="border"
          />
        </FormRow>
        <FormRow 
            className="ng-office-app__authed__content__body__item__curve-creator__row"
            isVertical
        >
          <Label>PRODUCT</Label>
          <QueryDropdown
              defaultValue={product}
              disabled={!groupName}
              fetchNextPage={fetchMoreProductValues}
              fixed
              isFetching={isFetchingProductValues || isFetchingNextPageProductValues}
              menuPlacement="bottom"
              menuPortalTarget={document.querySelector('.ng-office-app')}
              name="product"
              noError
              onChange={onChangeProduct}
              onInputChange={setProductValueQuery}
              openMenuOnFocus
              options={productValues}
              placeholder="Choose product"
              ref={productRef}
              variant="border"
          />
        </FormRow>
        <FormRow 
            className="ng-office-app__authed__content__body__item__curve-creator__row"
            isVertical
        >
          <Label>ROOT</Label>
          <QueryDropdown
              defaultValue={root}
              disabled={!product || !groupName}
              fetchNextPage={fetchMoreRootValues}
              fixed
              isFetching={isFetchingRootValues || isFetchingNextPageRootValues}
              menuPlacement="bottom"
              menuPortalTarget={document.querySelector('.ng-office-app')}
              name="root"
              noError
              onChange={onChangeRoot}
              onInputChange={setRootValueQuery}
              openMenuOnFocus
              options={rootValues}
              placeholder="Choose root"
              ref={rootRef}
              variant="border"
          />
        </FormRow>
        <div className="ng-office-app__authed__content__body__item__curve-creator__divider"/>
        <div className="ng-office-app__authed__content__body__item__curve-creator__submit">
          <Button
              disabled={!product || !root || !groupName}
              onClick={onAddItem}
              size="small"
              variant="primary"
          >
            <Add
                color={!product || !root || !groupName ? "grey-dark" : "white-base"}
                height={16}
                width={16}
            />
            <span>Add curve</span>
          </Button>
        </div>
      </Form>
    </Fragment>
  )
}

export default CurveCreator;